import React from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import { DynamicImage, Text } from 'components';
import { mobile, tablet, useQuery } from 'styles/breakpoints';
import { Typography } from 'utils/theme';

interface HeaderProps {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  imageAlt: string;
  optionsLabelFont?: Typography;
  imageHeightDesktop: string;
  imageHeightTablet: string;
  imageHeightMobile: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 3rem auto;
  align-items: center;
  max-width: 48rem;

  @media ${tablet} {
    margin: 1.5rem auto;
  }
`;

const StyledDynamicImage = styled(DynamicImage)<{
  imageHeightDesktop: string;
  imageHeightTablet: string;
  imageHeightMobile: string;
}>`
  height: ${({ imageHeightDesktop }) => imageHeightDesktop || '120px'};
  margin-bottom: 3rem;

  @media ${tablet} {
    height: ${({ imageHeightTablet }) => imageHeightTablet};
    margin-bottom: 1.5rem;
  }

  @media ${mobile} {
    height: ${({ imageHeightMobile }) => imageHeightMobile};
  }
`;

const Title = styled(Text).attrs({
  color: 'dark100',
})`
  text-align: center;
`;

const OptionsLabel = styled(Text).attrs({
  color: 'dark80',
})`
  margin-top: 1rem;
  max-width: 32.62rem;
  text-align: center;

  @media ${tablet} {
    margin-top: 0.75rem;
  }
`;

const Header: FC<HeaderProps> = ({
  title,
  subtitle,
  imageUrl,
  imageAlt,
  optionsLabelFont,
  imageHeightDesktop,
  imageHeightTablet,
  imageHeightMobile,
}) => {
  const { isTablet } = useQuery();
  return (
    <Container>
      {imageUrl ? (
        <StyledDynamicImage
          src={imageUrl}
          alt={imageAlt}
          imageHeightDesktop={imageHeightDesktop}
          imageHeightTablet={imageHeightTablet}
          imageHeightMobile={imageHeightMobile}
        />
      ) : null}
      <Title type={isTablet ? 'h3700' : 'h1700'}>{title}</Title>
      {subtitle ? (
        <OptionsLabel
          type={
            optionsLabelFont
              ? optionsLabelFont
              : isTablet
              ? 'captionM'
              : 'caption'
          }
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />
      ) : null}
    </Container>
  );
};

export default React.memo(Header);
